import { ref, computed, type Ref } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'
import { useSwipe } from '@/composables/swipe-action'
import { useDialogsStore } from '@/store/dialogs'

const TRIGGER_DISTANCE = 64
const MAX_PULL_DISTANCE = 96
const MIN_REFRESH_DURATION_MS = 500

function delay(ms: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, ms))
}

function applyResistance(rawDistance: number) {
  return (rawDistance * MAX_PULL_DISTANCE) / (rawDistance + MAX_PULL_DISTANCE)
}

export function usePullToRefresh(target: Ref<HTMLElement | undefined>) {
  const queryClient = useQueryClient()
  const dialogsStore = useDialogsStore()
  const isRefreshing = ref(false)
  const isDragging = ref(false)
  const pullDistance = ref(0)

  const isScrolledToTop = () => (target.value?.scrollTop ?? 0) <= 0

  function resetDrag() {
    if (isRefreshing.value) {
      return
    }
    isDragging.value = false
    pullDistance.value = 0
  }

  useSwipe(target, {
    onStartEvent: () => {
      if (isRefreshing.value || dialogsStore.hasOpenDialog) {
        return
      }
      isDragging.value = true
      pullDistance.value = 0
    },
    onMoveEvent: (event, { direction, distanceY }) => {
      if (
        isRefreshing.value ||
        dialogsStore.hasOpenDialog ||
        direction !== 'down' ||
        !isScrolledToTop()
      ) {
        pullDistance.value = 0
        return
      }
      event.preventDefault()
      pullDistance.value = applyResistance(distanceY)
    },
    onEndEvent: async () => {
      isDragging.value = false

      if (!isRefreshing.value && pullDistance.value >= TRIGGER_DISTANCE) {
        isRefreshing.value = true
        pullDistance.value = MAX_PULL_DISTANCE
        try {
          await Promise.all([
            queryClient.resetQueries({ type: 'active' }),
            delay(MIN_REFRESH_DURATION_MS)
          ])
        } finally {
          isRefreshing.value = false
        }
      }
      pullDistance.value = 0
    },
    onCancelEvent: resetDrag
  })

  const progress = computed(() =>
    Math.min(pullDistance.value / TRIGGER_DISTANCE, 1)
  )

  return { isRefreshing, isDragging, pullDistance, progress }
}
